/* Customised designs travel through the cart as a GROUP: one line per size,
 * plus service lines (screen setup, artwork redraw) tied to the group by
 * _setup_for / _redraw_for. The checkout guard validates every cart change and
 * refuses a group left incomplete — a job without its setup charge, a charge
 * without its job, a screen job under its minimum.
 *
 * So a removal has to land on a valid END state in one request:
 *   - one size of a design that keeps other sizes → that line alone (the guard
 *     allows it while the minimum holds);
 *   - a service line, or the last size of a design → the whole group, since
 *     every state in between is one the guard refuses;
 *   - a single removal the guard refuses anyway (the job would fall under its
 *     minimum) → offered as a whole-design removal, never done silently.
 * The first version removed the whole group for ANY member, which deleted a
 * six-size design when the customer took one size off.
 *
 * SHARED SCREEN RUNS (15 Oct 2026): adult garment lines carry _run — the id of the
 * screen-print run each position belongs to — and a setup line carries _setup_runs,
 * the runs it paid for. Two designs with the same run id share one setup and one
 * minimum, so a removal must respect the run as it respects the redraw line: a setup
 * line stays behind while another design still carries its run, removing a setup
 * line takes every design it pays for, and a design that cannot leave without
 * dropping the run under its minimum is offered as a whole-run removal. */
(function () {
  "use strict";

  function rootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function groupOf(props) {
    props = props || {};
    return props._grp || props._setup_for || props._redraw_for || null;
  }

  /** A setup or redraw charge: tied to a group but not a garment line itself. */
  function isService(props) {
    props = props || {};
    return !props._grp && !!(props._setup_for || props._redraw_for);
  }

  // ── Shared screen runs ───────────────────────────────────────────────────
  /** "front:hex:1:232x136,back:…" → ["hex:1", …] — the runs a garment line is in. */
  function runIds(props) {
    var out = [];
    String((props || {})._run || "").split(",").forEach(function (part) {
      var p = part.split(":");
      if (p.length >= 3 && p[1] && p[2]) out.push(p[1] + ":" + p[2]);
    });
    return out;
  }
  /** "hex:1,hex:2" → the runs a setup line pays for. */
  function setupRunIds(props) { return String((props || {})._setup_runs || "").split(",").filter(Boolean); }
  /** Groups (other than exceptGrp, ignoring the lines in excludeKeys) whose garments carry any of ids. */
  function groupsCarrying(items, ids, exceptGrp, excludeKeys) {
    var out = [];
    if (!ids.length) return out;
    items.forEach(function (it) {
      var p = it.properties || {};
      if (!p._grp || p._grp === exceptGrp || out.indexOf(p._grp) >= 0) return;
      if (excludeKeys && excludeKeys.indexOf(it.key) >= 0) return;
      if (intersects(runIds(p), ids)) out.push(p._grp);
    });
    return out;
  }
  /** Every other group sharing a run with grp, transitively — a chain of shared runs is one lot. */
  function runNeighbours(items, grp) {
    var seen = {}; seen[grp] = true;
    var queue = [grp];
    while (queue.length) {
      var g = queue.shift(), ids = [];
      items.forEach(function (it) { var p = it.properties || {}; if (p._grp === g) runIds(p).forEach(function (id) { if (ids.indexOf(id) < 0) ids.push(id); }); });
      groupsCarrying(items, ids, null).forEach(function (o) { if (!seen[o]) { seen[o] = true; queue.push(o); } });
    }
    delete seen[grp];
    return Object.keys(seen);
  }
  function anyKeyOf(items, grp) {
    for (var i = 0; i < items.length; i++) if (items[i].properties && items[i].properties._grp === grp) return items[i].key;
    for (var j = 0; j < items.length; j++) if (groupOf(items[j].properties) === grp) return items[j].key;
    return null;
  }

  function find(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) if (items[i].key === key) return items[i];
    return null;
  }

  /**
   * Every line key belonging to the same design group as `key`. Falls back to just
   * `key`. The group's artwork-redraw line stays behind when ANOTHER design in the
   * cart is riding on it (_redraw_covered): the checkout guard keeps that line in
   * the cart for the covered design, so zeroing it here would have the whole
   * removal refused — with a message about a different item.
   */
  function groupKeys(cart, key) {
    var items = (cart && cart.items) || [];
    var me = find(cart, key);
    var grp = me ? groupOf(me.properties) : null;
    if (!grp) return [key];
    // Which artwork this group's redraw line pays for, and whether a covered design
    // in ANOTHER group relies on that same artwork (matched on _redraw_art ids; a
    // line with no ids recorded is taken to cover anything).
    var myArt = {};
    for (var a = 0; a < items.length; a++) {
      var ap = items[a].properties || {};
      if (ap._redraw_for && groupOf(ap) === grp) String(ap._redraw_art || "").split(",").forEach(function (id) { if (id) myArt[id] = true; });
    }
    var coveredElsewhere = false;
    for (var c = 0; c < items.length; c++) {
      var cp = items[c].properties || {};
      if (cp._redraw_covered !== "1" || groupOf(cp) === grp) continue;
      var ids = String(cp._redraw_art || "").split(",").filter(Boolean);
      var shares = !Object.keys(myArt).length || !ids.length;
      for (var s = 0; s < ids.length && !shares; s++) if (myArt[ids[s]]) shares = true;
      if (shares) { coveredElsewhere = true; break; }
    }
    var keys = [];
    for (var j = 0; j < items.length; j++) {
      var pj = items[j].properties || {};
      if (groupOf(pj) !== grp) continue;
      if (coveredElsewhere && pj._redraw_for && items[j].key !== key) continue; // paid for by this group, needed by another
      // A setup line whose runs another design still carries stays behind the same
      // way: the guard keeps it in the cart for that design, and zeroing it here
      // would have the whole removal refused.
      if (pj._setup_for && items[j].key !== key && groupsCarrying(items, setupRunIds(pj), grp).length) continue;
      keys.push(items[j].key);
    }
    // A redraw line kept behind after its own design left (see above) is an orphan
    // the guard tolerates only while some covered design still rides on it. Two
    // dead ends followed from that: removing the last such design left the orphan
    // alone (refused as a leftover), and removing the orphan left the design
    // unpaid (refused as "relies on a redraw"). Both must land in ONE request.
    var mp = (me && me.properties) || {};
    if (mp._redraw_for) {
      // Target is a redraw line (orphan or not): take the covered designs it alone
      // pays for, or the guard refuses the removal as leaving them unpaid.
      var myIds = artIds(mp);
      coveredGroups(items).forEach(function (cg) {
        if (cg.grp === grp) return;
        if (myIds.length && !intersects(cg.art, myIds)) return;
        if (hasOtherPayer(items, cg, key)) return;
        for (var g = 0; g < items.length; g++) if (groupOf(items[g].properties) === cg.grp && keys.indexOf(items[g].key) < 0) keys.push(items[g].key);
      });
    } else {
      // Target is a design: take any orphan redraw line nothing else will rely on.
      var remaining = coveredGroups(items).filter(function (cg) { return cg.grp !== grp; });
      items.forEach(function (it) {
        var ip = it.properties || {};
        if (!ip._redraw_for || hasGarments(items, groupOf(ip)) || keys.indexOf(it.key) >= 0) return;
        var ids = artIds(ip);
        var needed = ids.length ? remaining.some(function (cg) { return intersects(cg.art, ids); }) : remaining.length > 0;
        if (!needed) keys.push(it.key);
      });
      // Likewise an orphan SETUP line — its own design already gone, kept because this
      // design carried a run it paid for. Once we leave nothing rides on it, and the
      // guard refuses a cart holding a leftover charge, so it comes with us — unless a
      // third design still carries one of its runs.
      items.forEach(function (it) {
        var ip = it.properties || {};
        if (!ip._setup_for || hasGarments(items, ip._setup_for) || keys.indexOf(it.key) >= 0) return;
        if (groupsCarrying(items, setupRunIds(ip), grp).length) return;
        keys.push(it.key);
      });
    }
    // Target is a setup line paying for shared runs: every design still carrying one
    // of them goes too, or the guard refuses the removal as leaving it unpaid.
    if (mp._setup_for && setupRunIds(mp).length) {
      groupsCarrying(items, setupRunIds(mp), grp).forEach(function (og) {
        var k0 = anyKeyOf(items, og);
        if (k0) groupKeys(cart, k0).forEach(function (k) { if (keys.indexOf(k) < 0) keys.push(k); });
      });
    }
    return keys.length ? keys : [key];
  }

  function artIds(props) { return String((props || {})._redraw_art || "").split(",").filter(Boolean); }
  function intersects(arr, ids) { for (var i = 0; i < ids.length; i++) if (arr.indexOf(ids[i]) >= 0) return true; return false; }
  function hasGarments(items, grp) {
    for (var i = 0; i < items.length; i++) if (items[i].properties && items[i].properties._grp === grp) return true;
    return false;
  }
  /** Covered designs in the cart: [{ grp, art: [ids] }]. */
  function coveredGroups(items) {
    var by = {};
    items.forEach(function (it) {
      var p = it.properties || {};
      if (p._redraw_covered !== "1" || !p._grp) return;
      by[p._grp] = by[p._grp] || { grp: p._grp, art: [] };
      artIds(p).forEach(function (id) { if (by[p._grp].art.indexOf(id) < 0) by[p._grp].art.push(id); });
    });
    return Object.keys(by).map(function (k) { return by[k]; });
  }
  /**
   * Would this covered design still be fully paid without the line `excludeKey`?
   * The guard needs EVERY id in its art paid by some redraw line, so each id the
   * leaving line covered must be carried by another line (an untagged legacy
   * line counts for anything).
   */
  function hasOtherPayer(items, cg, excludeKey) {
    var leaving = null;
    for (var i = 0; i < items.length; i++) if (items[i].key === excludeKey) leaving = artIds(items[i].properties);
    var need = (leaving && leaving.length) ? cg.art.filter(function (id) { return leaving.indexOf(id) >= 0; }) : cg.art.slice();
    if (!need.length) need = cg.art.slice();
    var covered = {};
    for (var j = 0; j < items.length; j++) {
      var p = items[j].properties || {};
      if (!p._redraw_for || items[j].key === excludeKey) continue;
      var ids = artIds(p);
      if (!ids.length) return true; // legacy line: pays for anything
      ids.forEach(function (id) { covered[id] = true; });
    }
    for (var k = 0; k < need.length; k++) if (!covered[need[k]]) return false;
    return true;
  }

  /** What zeroing `key` should actually zero: the line alone, or its whole group. */
  function removalKeys(cart, key) {
    var me = find(cart, key);
    var grp = me ? groupOf(me.properties) : null;
    if (!grp) return [key];
    if (isService(me.properties)) return groupKeys(cart, key);
    var items = (cart && cart.items) || [];
    var garments = 0;
    for (var j = 0; j < items.length; j++) {
      if (items[j].properties && items[j].properties._grp === grp) garments++;
    }
    return garments <= 1 ? groupKeys(cart, key) : [key];
  }

  function fetchCart() {
    return fetch(rootPath() + "cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); });
  }

  function post(updates) {
    return fetch(rootPath() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); });
  }

  function zeroAll(keys) {
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    return post(updates);
  }

  /** Zero every key of the group `key` belongs to, in one request. */
  function removeGroup(key) {
    return fetchCart().then(function (cart) { return zeroAll(groupKeys(cart, key)); });
  }

  /**
   * Remove `key` the least destructive way. Resolves { ok, d } like a plain
   * update, or { ok:false, cancelled:true } when the customer declined the
   * whole-design fallback.
   */
  function remove(key) {
    return fetchCart().then(function (cart) {
      var keys = removalKeys(cart, key);
      // Removing a service line can only land validly by taking the design(s) it
      // charges for — never silently. Both fee lines qualify: a setup line removal
      // takes its whole group (isService → groupKeys), which is a four-figure cart
      // disappearing on one click, and only the redraw line used to ask.
      var me = find(cart, key), mp = (me && me.properties) || {};
      if ((mp._redraw_for || mp._setup_for) && keys.length > 1) {
        var designs = 0;
        keys.forEach(function (k) { var it = find(cart, k); if (it && it.properties && it.properties._grp) designs++; });
        var what = mp._setup_for ? "screen setup charge" : "artwork redraw";
        if (designs && !window.confirm("This " + what + " is what pays for the printing on " + designs + " item" + (designs === 1 ? "" : "s") + " in your cart. Removing it removes " + (designs === 1 ? "that item" : "those items") + " too. Continue?")) {
          return Promise.resolve({ ok: false, cancelled: true });
        }
      }
      return zeroAll(keys).then(function (res) {
        if (res.ok) return trimKeptSetups(cart, keys).then(function () { return res; });
        if (keys.length > 1) return offerRun(cart, key, res);
        var all = groupKeys(cart, key);
        if (all.length <= 1) return offerRun(cart, key, res); // a one-line design another design rides on: the only exit is the whole run
        // The guard refused the single removal (the job would fall under its
        // minimum, say). The only valid alternative is the whole design — the
        // customer's call, with the guard's own reason in front of them.
        var why = (res.d && res.d.description) || "";
        var ok = window.confirm((why ? why + "\n\n" : "") + "Remove the whole design from your cart instead?");
        if (!ok) return { ok: false, cancelled: true, d: res.d };
        return zeroAll(all).then(function (r2) {
          if (r2.ok) return trimKeptSetups(cart, all).then(function () { return r2; });
          return offerRun(cart, key, r2);
        });
      });
    });
  }

  /**
   * The whole design could not go either: the garments it shares a run with would be
   * left under the run's minimum. The only valid end state is every design in the run
   * leaving together — the customer's call, with the guard's reason in front of them.
   */
  function offerRun(cart, key, res) {
    var items = (cart && cart.items) || [];
    var me = find(cart, key), grp = me ? groupOf(me.properties) : null;
    if (!grp) return res;
    var lot = runNeighbours(items, grp);
    if (!lot.length) return res;
    var keys = [];
    var add = function (k) { if (keys.indexOf(k) < 0) keys.push(k); };
    [grp].concat(lot).forEach(function (g) {
      var k0 = anyKeyOf(items, g);
      if (k0) groupKeys(cart, k0).forEach(add);
      // Every group in the lot leaves, so their setup lines go too (groupKeys keeps one
      // while another group carries its runs — here that group is leaving as well).
      items.forEach(function (it) { var p = it.properties || {}; if (p._setup_for === g) add(it.key); });
    });
    // An orphan setup line (its own design long gone) kept because members of this
    // lot carried its runs: with the whole lot leaving, nothing rides on it — it goes
    // too, or the guard refuses the cart as holding a leftover charge.
    var leaving = [grp].concat(lot);
    items.forEach(function (it) {
      var p = it.properties || {};
      if (!p._setup_for || hasGarments(items, p._setup_for) || keys.indexOf(it.key) >= 0) return;
      var carriers = groupsCarrying(items, setupRunIds(p), null);
      for (var c = 0; c < carriers.length; c++) if (leaving.indexOf(carriers[c]) < 0) return;
      add(it.key);
    });
    var designs = 0;
    keys.forEach(function (k) { var it = find(cart, k); if (it && it.properties && it.properties._grp) designs++; });
    var why = (res.d && res.d.description) || "";
    var ok = window.confirm((why ? why + "\n\n" : "") + "These items share one screen print run, so they can only be removed together. Remove all " + designs + " item" + (designs === 1 ? "" : "s") + " in the run?");
    if (!ok) return { ok: false, cancelled: true, d: res.d };
    return zeroAll(keys);
  }

  /**
   * After a removal that kept a setup line behind (its runs still carried by another
   * design): if some of its runs left with the removed lines, bring the line down to
   * the screens still in use. Best effort — the cart is valid either way; this only
   * stops the remaining design paying for screens nobody prints. One request per
   * line, each a complete state the guard accepts (declared screens come down with
   * the quantity, and the guard trusts the declared count only upwards).
   */
  function trimKeptSetups(cart, removedKeys) {
    var items = (cart && cart.items) || [];
    var work = [];
    items.forEach(function (it) {
      var p = it.properties || {};
      if (!p._setup_for || removedKeys.indexOf(it.key) >= 0) return;
      var ids = setupRunIds(p);
      if (!ids.length) return;
      var still = ids.filter(function (id) { return groupsCarrying(items, [id], null, removedKeys).length > 0; });
      if (!still.length || still.length === ids.length) return;
      var screens = 0;
      still.forEach(function (id) { screens += parseInt(id.split(":")[1], 10) || 1; });
      var props = {};
      for (var k in p) props[k] = p[k];
      props._setup_runs = still.join(",");
      props._setup_screens = String(screens);
      props.Service = "Screen print setup — " + screens + " screen" + (screens === 1 ? "" : "s");
      work.push({ id: it.key, quantity: screens, properties: props });
    });
    return work.reduce(function (chain, w) {
      return chain.then(function () {
        return fetch(rootPath() + "cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(w),
        }).catch(function () {});
      });
    }, Promise.resolve());
  }

  window.tpgCartGroupKeys = groupKeys;
  window.tpgCartRemovalKeys = removalKeys;
  window.tpgCartRemoveGroup = removeGroup;
  window.tpgCartRemove = remove;

  // Cart page: a Remove link marked data-tpg-remove goes through remove(), then reloads.
  document.addEventListener("click", function (e) {
    var a = e.target && e.target.closest && e.target.closest("a[data-tpg-remove]");
    if (!a) return;
    e.preventDefault();
    var key = a.getAttribute("data-tpg-remove");
    a.setAttribute("aria-busy", "true");
    remove(key).then(function (res) {
      if (res.ok) { window.location.reload(); return; }
      a.removeAttribute("aria-busy");
      if (res.cancelled) return;
      alert((res.d && res.d.description) || "Could not remove this item.");
    }).catch(function () {
      a.removeAttribute("aria-busy");
      window.location.href = a.getAttribute("href"); // fall back to the plain remove link
    });
  }, true);
})();
